import { PATH } from '@/app/routes';
import { MenuIcon } from '@/assets';
import { DemoModeNote } from '@/features';
import { AuthModal } from '@/features/auth';
import { useCatalogData } from '@/app/context';
import { useLocation, useNavigate } from 'react-router-dom';
import { BrandLogo } from '@/shared/components';
import { CatalogMenu } from '@/features/catalog';
import { FC, useEffect, useMemo, useState } from 'react';
import { cn, getNonEmptyCategories, isTelegramWebApp } from '@/shared/lib';
import { ShoppingBagIcon, UserIcon, XIcon } from 'lucide-react';
import { selectAuthInitialized, useAppSelector } from '@/app/store';
import { useScreenWidth } from '@/shared/hooks';
import { CatalogReservationButton } from '@/widgets/catalog/CatalogReservationButton';

export const MinimalisticHeader: FC = () => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isTgWebApp = isTelegramWebApp();
  const { isTablet, isDesktop } = useScreenWidth();
  const isAuthenticated = useAppSelector(selectAuthInitialized);
  const { hasLegalInfo } = useAppSelector((state) => state.application.demoMode);
  const numberOfProducts = useAppSelector((state) => state.basket.numberOfProducts);
  const brandLogo = useAppSelector((state) => state.initial.brendLogo);
  const showBrandLogo = useAppSelector((state) => state.initial.settings.showBrandLogo);

  const [menuOpen, setMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const [menuVisible, setMenuVisible] = useState(false);

  const { categories, menuItems, isCatalogLoading, isBasketFetching, reservationUrl } = useCatalogData();
  const notEmptyCategories = useMemo(() => getNonEmptyCategories(categories, menuItems), [categories, menuItems]);

  const buttonDisabled = isCatalogLoading || isBasketFetching;

  useEffect(() => {
    if (menuOpen) {
      setMenuVisible(true);
      return;
    }

    const timeout = setTimeout(() => setMenuVisible(false), 300);
    return () => clearTimeout(timeout);
  }, [menuOpen]);

  const hasLogo = Boolean(brandLogo && showBrandLogo);
  const badgeLabel = numberOfProducts > 99 ? '99+' : String(numberOfProducts);

  const onUserClick = () => {
    setMenuOpen(false);
    if (isAuthenticated) nav(PATH.profile);
    else if (!isTgWebApp) setAuthModalOpen(true);
  };

  const onBasketClick = () => {
    setMenuOpen(false);
    nav(PATH.basket);
  };

  const onLogoClick = () => {
    setMenuOpen(false);
    if (pathname !== PATH.catalog) nav(PATH.catalog);
  };

  return (
    <div className={cn('relative flex w-full flex-col bg-background', menuVisible && 'max-md:z-[60]')}>
      <DemoModeNote variant='alert' className='h-[var(--height-demo-note)] w-full' />

      <div className='relative flex h-[var(--height-header-row)] w-full items-center justify-between px-4 lg:px-8'>
        <button
          type='button'
          aria-label='menu'
          onClick={() => setMenuOpen((prev) => !prev)}
          className='shrink-0'
          disabled={buttonDisabled}
        >
          {menuVisible && !isTablet ? (
            <XIcon className='size-[26px] text-foreground cursor-pointer' strokeWidth={1.33} />
          ) : (
            <MenuIcon className='size-[26px] text-foreground cursor-pointer' />
          )}
        </button>

        <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
          {hasLogo && <BrandLogo onClick={onLogoClick} width={isDesktop ? 195 : 130} height={isDesktop ? 48 : 32} />}
        </div>

        <div className='flex shrink-0 items-center gap-3'>
          {reservationUrl && isDesktop && (
            <CatalogReservationButton isMinimalistic reservationUrl={reservationUrl} className='w-[223px]' />
          )}

          {hasLegalInfo && (
            <button type='button' aria-label='profile' onClick={onUserClick} disabled={buttonDisabled}>
              <UserIcon className='size-[26px] text-foreground cursor-pointer' strokeWidth='1.33' />
            </button>
          )}

          <button
            type='button'
            aria-label='basket'
            onClick={onBasketClick}
            disabled={numberOfProducts === 0 || buttonDisabled}
            className={cn('relative', numberOfProducts > 0 && 'cursor-pointer')}
          >
            <ShoppingBagIcon
              className={cn('size-[26px] text-foreground', numberOfProducts === 0 && 'text-muted-foreground')}
              strokeWidth='1.33'
            />
            {numberOfProducts > 0 && (
              <span
                className={cn(
                  'absolute -right-1.5 -top-1 flex h-3.5 min-w-3.5 items-center justify-center',
                  'rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-background',
                )}
              >
                {badgeLabel}
              </span>
            )}
          </button>
        </div>
      </div>

      <CatalogMenu open={menuOpen} onOpenChange={setMenuOpen} categories={notEmptyCategories} />

      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
    </div>
  );
};
